import { DUPLICATED_VACANCY_EXTERNAL_URL_PLACEHOLDER, Vacancy } from '@mkp/vacancy/models';

/**
 * `Vacancy` guarantees these three lists but the API contract does not, and consumers
 * dereference them unguarded: `toJson` maps over `languageRequirements`, the preview reads
 * `.length` on all three. Keeping them optional here forces `fromJson` to default them.
 */
type VacancyJson = Omit<
  Vacancy,
  'recruiterId' | 'recruiter' | 'benefits' | 'languageRequirements' | 'skillRequirements'
> & {
  benefits?: Vacancy['benefits'] | null;
  languageRequirements?: Vacancy['languageRequirements'] | null;
  skillRequirements?: Vacancy['skillRequirements'] | null;
  videos: { url: string }[];
  atsSettings?: { externalApplicationUrl?: string; screeningQuestions?: [string] | null };
  recruiterId?: string | null;
  recruiter?: { phoneNumber?: string | null } | null;
};

// Duplicating into an external-ATS workspace writes a placeholder link, because Core API
// rejects a create without one. Dropping it here is what keeps every read site — preview,
// list, CTAs, application counts — from having to recognise it, and makes the edit form load
// an empty required field so the user has to supply the real link before publishing.
const readExternalUrl = (externalApplicationUrl: string | undefined): string | null =>
  !externalApplicationUrl || externalApplicationUrl === DUPLICATED_VACANCY_EXTERNAL_URL_PLACEHOLDER
    ? null
    : externalApplicationUrl;

export class VacancySerializer {
  static fromJson(json: VacancyJson): Vacancy {
    const recruiterId = json.recruiterId ?? null;
    const phoneNumber = recruiterId ? (json.recruiter?.phoneNumber ?? null) : null;

    return {
      _embedded: json._embedded,
      _version: json._version,
      accountId: json.accountId,
      address: json.address,
      addresses: json.addresses ?? (json.address ? [json.address] : []),
      applicationCount: json.applicationCount,
      benefits: json.benefits ?? [],
      companyName: json.companyName,
      createdAt: json.createdAt,
      createdBy: json.createdBy,
      description: json.description,
      employmentGradeFrom: json.employmentGradeFrom,
      employmentGradeTo: json.employmentGradeTo,
      experience: json.experience,
      externalSource: json.externalSource,
      headerMediaApiId: json.headerMediaApiId,
      id: json.id,
      jobPosition: json.jobPosition,
      languageRequirements: json.languageRequirements ?? [],
      locationId: json.locationId,
      logoMediaApiId: json.logoMediaApiId,
      videoUrl: json.videos?.[0]?.url ?? '',
      metadataApiEducationLevelId: json.metadataApiEducationLevelId,
      metadataApiEmploymentPositionId: json.metadataApiEmploymentPositionId,
      metadataApiEmploymentTypeId: json.metadataApiEmploymentTypeId,
      newApplicant: json.newApplicant,
      salaryFrom: json.salaryFrom,
      salaryTo: json.salaryTo,
      salaryUnit: json.salaryUnit,
      showSalary: json.showSalary,
      showSalaryEstimation: json.showSalaryEstimation,
      skillRequirements: json.skillRequirements ?? [],
      screeningQuestionRequirements:
        json.atsSettings?.screeningQuestions?.map((question, index) => ({
          question,
          index,
        })) ?? [],
      updatedAt: json.updatedAt,
      updatedBy: json.updatedBy,
      externalUrl: readExternalUrl(json.atsSettings?.externalApplicationUrl),
      recruiterId,
      recruiter: phoneNumber ? { phoneNumber } : null,
      jobGroupId: json.jobGroupId ?? null,
      _links: json._links,
    };
  }

  static toJson(vacancy: Vacancy): { [key: string]: unknown } {
    return {
      _embedded: vacancy._embedded,
      _version: vacancy._version,
      accountId: vacancy.accountId,
      address: vacancy.address,
      ...(vacancy.addresses?.length ? { addresses: vacancy.addresses } : {}),
      benefits: vacancy.benefits,
      description: vacancy.description,
      employmentGradeFrom: vacancy.employmentGradeFrom,
      employmentGradeTo: vacancy.employmentGradeTo,
      experience: vacancy.experience,
      headerMediaApiId: vacancy.headerMediaApiId,
      id: vacancy.id,
      jobPosition: vacancy.jobPosition,
      languageRequirements: vacancy.languageRequirements.map((requirement) => ({
        metadataApiLanguageId: requirement.metadataApiLanguageId,
        metadataApiLanguageLevelId:
          requirement.metadataApiLanguageLevelId && ~~requirement.metadataApiLanguageLevelId,
      })),
      locationId: vacancy.locationId,
      logoMediaApiId: vacancy.logoMediaApiId,
      videos: vacancy.videoUrl ? [{ url: vacancy.videoUrl }] : null,
      metadataApiEducationLevelId:
        vacancy.metadataApiEducationLevelId && ~~vacancy.metadataApiEducationLevelId,
      metadataApiEmploymentPositionId:
        vacancy.metadataApiEmploymentPositionId && ~~vacancy.metadataApiEmploymentPositionId,
      metadataApiEmploymentTypeId:
        vacancy.metadataApiEmploymentTypeId && ~~vacancy.metadataApiEmploymentTypeId,
      salaryFrom: vacancy.salaryFrom && ~~vacancy.salaryFrom,
      salaryTo: vacancy.salaryTo && ~~vacancy.salaryTo,
      salaryUnit: vacancy.salaryUnit,
      showSalary: vacancy.showSalary,
      showSalaryEstimation: vacancy.showSalaryEstimation,
      skillRequirements: vacancy.skillRequirements,
      atsSettings: {
        screeningQuestions:
          vacancy.screeningQuestionRequirements?.map(({ question }) => question) ?? [],
        externalApplicationUrl: vacancy.externalUrl ?? null,
      },
      recruiterId: vacancy.recruiterId ?? null,
      recruiter:
        vacancy.recruiterId && vacancy.recruiter?.phoneNumber
          ? {
              firstName: null,
              lastName: null,
              phoneNumber: vacancy.recruiter.phoneNumber,
            }
          : null,
      jobGroupId: vacancy.jobGroupId ?? null,
    };
  }
}
